import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { api } from '../api';
import { formatDateTimeFr } from '../methods';
import { ArticleResponse } from '../types';
import { toast } from 'react-toastify';

const ArticleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false); // ✅ État pour le dialog

  useEffect(() => {
    api
      .get(`/articles/${id}`)
      .then((res) => setArticle(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = () => {
    setOpenDialog(false)
    api
      .delete(`/articles/${id}`)
      .then(() => {
        toast.success("Delete article success")
        navigate('/articles'); //

      })
      .catch((err) => {
        toast.error("Delete article failed")
        console.error('Erreur lors de la suppression :', err);

      })
      ;

  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="error">
          Article not found.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Card sx={{ maxWidth: 800, margin: 'auto', mt: 4, p: 2, position: 'relative' }}>
        <Box position="absolute" top={16} right={16}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/articles/${id}/edit/`)}
            sx={{ mr: 2 }}
          >
            EDIT
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setOpenDialog(true)} // ✅ Ouvre le dialog
          >
            DELETE
          </Button>
        </Box>

        {article.image && (
          <CardMedia
            component="img"
            height="300"
            image={article.image}
            alt="Article"
            sx={{ objectFit: 'contain', mb: 2, mt: 5 }}
          />
        )}

        <CardContent>
          <Typography variant="h5" gutterBottom>
            ID: {article.id}
          </Typography>
          <Typography variant="h5" gutterBottom>
            Description: {article.description}
          </Typography>

          <Typography variant="h5" gutterBottom>
            Type: {article.type}
          </Typography>
          <Typography variant="h5" gutterBottom>
            Tag: {article.tag}
          </Typography>


          <Typography variant="h5">
            Link: <Link href={article.link} target="_blank" rel="noopener">Open</Link>
          </Typography>


          <Typography variant="h5" gutterBottom>
            Target: {article.target_id ? "OUI" : "NON"}
          </Typography>


          <Typography variant="body2" color="text.secondary" gutterBottom>
            Modified At: {formatDateTimeFr(article.modified_at)}
          </Typography>



        </CardContent>
      </Card>

      {/* ✅ Dialog de confirmation */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Confirm Delete Article</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this article? This action cannot be undone
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Confirm</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArticleDetails;
